/**
 * ActivityRail — the Heute right context rail (Story UI-U3, AC 2): a Fortschritt
 * header, a scrollable Aktivität feed, and a collapsible Kontext block. Ported
 * from the reference prototype's agent-rail.tsx / activity-feed.tsx.
 *
 * Sticky, xl:+ only — it lives INSIDE the screen layout, not the app shell.
 * Empty (live mode — the tenant audit feed is admin-permission-gated, so we do
 * NOT wire it here): a design-conform empty state.
 *
 * [Source: brokly-design-reference/components/brokly/agent-rail.tsx]
 * [Source: apps/api/src/modules/audit/audit.controller.ts (audit.read gate — not wired here)]
 */
import { useState } from 'react';
import { Bot, ChevronRight, Activity, ChevronDown } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { cn } from '../../../lib/cn';
import type { HeuteActivityItem } from '../heute-mock';

export function ActivityRail({ items }: { items: HeuteActivityItem[] }) {
  const [kontextOpen, setKontextOpen] = useState(true);
  const hasActivity = items.length > 0;

  return (
    <aside className="sticky top-4 hidden h-fit w-72 shrink-0 flex-col gap-4 xl:flex">
      {/* Fortschritt header */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <Activity className="size-4 text-primary" /> Fortschritt
        </div>
        <Card className="p-3">
          {hasActivity ? (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Heute erledigt</span>
                <span className="mono-num text-foreground">12 / 18</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-primary" style={{ width: '66%' }} />
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Noch keine Aufgaben heute.</p>
          )}
        </Card>
      </div>

      {/* Aktivität feed */}
      <div>
        <div className="mb-2 text-sm font-medium">Aktivität</div>
        <Card className="max-h-80 divide-y divide-hairline overflow-y-auto">
          {hasActivity ? (
            items.map((a, i) => (
              <div key={i} className="flex gap-3 p-3">
                <div className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <Bot className="size-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs leading-snug">
                    <span className="font-medium">Brokly KI</span> {a.txt}
                  </p>
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                    <ChevronRight className="size-3" />
                    <span className="mono-num">{a.ref}</span> · {a.t}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center gap-1 p-6 text-center text-xs text-muted-foreground">
              <Bot className="size-5 opacity-60" />
              Noch keine Aktivität.
            </div>
          )}
        </Card>
      </div>

      {/* Collapsible Kontext */}
      <div>
        <button
          type="button"
          onClick={() => setKontextOpen((o) => !o)}
          aria-expanded={kontextOpen}
          className="mb-2 flex w-full items-center justify-between text-sm font-medium text-foreground"
        >
          Kontext
          <ChevronDown className={cn('size-4 transition-transform', !kontextOpen && '-rotate-90')} />
        </button>
        {kontextOpen && (
          <Card className="p-3 text-xs leading-relaxed text-muted-foreground">
            Brokly priorisiert deine Fälligkeiten nach Frist und Prämienvolumen. Entscheide oben
            werden nach Wirkung sortiert.
          </Card>
        )}
      </div>
    </aside>
  );
}
